/**
 * Medical Staff API Client
 * 의료진 관리 API 클라이언트
 */

import apiClient from './client';

export interface StaffAvailability {
  id?: number;
  dayOfWeek: number; // 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토
  dayOfWeekName?: string;
  startTime: string; // "HH:mm" 형식
  endTime: string;
  breakStartTime?: string;
  breakEndTime?: string;
}

export interface MedicalStaff {
  id?: number;
  organizationId?: number;
  name: string;
  position?: string;
  specialty?: string;
  history?: string;
  licenseNumber?: string;
  licenseType?: string;
  isActive?: boolean;
  deactivatedAt?: string;
  deactivatedById?: number;
  deactivatedByName?: string;
  createdAt?: string;
  updatedAt?: string;
  availabilities?: StaffAvailability[];
  closedDates?: string[]; // 특정 휴무일 (["2025-01-01", "2025-01-15"])
}

export interface MedicalStaffCreateDto {
  name: string;
  position?: string;
  specialty?: string;
  history?: string;
  licenseNumber?: string;
  licenseType?: string;
  availabilities?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    breakStartTime?: string;
    breakEndTime?: string;
  }[];
  closedDates?: string[]; // 특정 휴무일
}

export interface MedicalStaffUpdateDto {
  name: string;
  position?: string;
  specialty?: string;
  history?: string;
  licenseNumber?: string;
  licenseType?: string;
  availabilities?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    breakStartTime?: string;
    breakEndTime?: string;
  }[];
  closedDates?: string[]; // 특정 휴무일
}

export const medicalStaffApi = {
  /**
   * 의료진 목록 조회
   */
  getAll: async (includeInactive = false): Promise<MedicalStaff[]> => {
    const params = includeInactive ? '?includeInactive=true' : '';
    const response = await apiClient.get<MedicalStaff[]>(`/api/medical-staff${params}`);
    return response.data;
  },

  /**
   * 의료진 상세 조회
   */
  getById: async (id: number): Promise<MedicalStaff> => {
    const response = await apiClient.get<MedicalStaff>(`/api/medical-staff/${id}`);
    return response.data;
  },

  /**
   * 의료진 생성
   */
  create: async (data: MedicalStaffCreateDto): Promise<MedicalStaff> => {
    const response = await apiClient.post<MedicalStaff>('/api/medical-staff', data);
    return response.data;
  },

  /**
   * 의료진 수정
   */
  update: async (id: number, data: MedicalStaffUpdateDto): Promise<MedicalStaff> => {
    const response = await apiClient.put<MedicalStaff>(`/api/medical-staff/${id}`, data);
    return response.data;
  },

  /**
   * 의료진 퇴사 처리 (Soft delete)
   */
  deactivate: async (id: number): Promise<void> => {
    await apiClient.patch(`/api/medical-staff/${id}/deactivate`);
  },

  /**
   * 의료진 재활성화
   */
  reactivate: async (id: number): Promise<void> => {
    await apiClient.patch(`/api/medical-staff/${id}/reactivate`);
  },

  /**
   * 의료진 완전 삭제 (ADMIN only, 담당 이력 없을 때만)
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/medical-staff/${id}`);
  },
};
